import { Component, OnInit, ViewChild, TemplateRef, ChangeDetectorRef, HostListener, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { mobiscroll, MbscDatetimeOptions } from '@mobiscroll/angular';
import * as alertifyjs from 'alertifyjs';
import Swal from 'sweetalert2';
import { saveAs } from 'file-saver';

import { UserService } from 'src/app/services/user/user.service';
import { AuthService } from 'src/app/services/auth/auth.service';

mobiscroll.settings = {
  lang: 'es',
  theme: 'ios',
  themeVariant: 'light',
};

const now = new Date();

/**
 * Vista de tabla paginada de pagos de Finanzas (en paralelo a PaymentsComponent).
 * - Mismos filtros de siempre que la vista clásica (rango de fecha, estatus, forma de
 *   pago, sub estatus, multiselect de productos y orden) + mismo endpoint
 *   (POST /api/invoice-credit/payments).
 * - Filas livianas; el detalle completo se reusa abriendo <app-person-payments> en modo modal.
 * - Quick actions Aprobar/Anular/Editar replicando los guards de la vista clásica.
 * La vista clásica (PaymentsComponent) se mantiene intacta; cuál se muestra lo decide
 * la preferencia default_finance_payments_view (Parámetros de Sistema > Preferencias).
 */
@Component({
  selector: 'app-finance-payments-table',
  templateUrl: './finance-payments-table.component.html',
  styleUrls: ['./finance-payments-table.component.css'],
  providers: [DatePipe]
})
export class FinancePaymentsTableComponent implements OnInit {

  @ViewChild('detailModal') detailModal!: TemplateRef<any>;

  banks: any = {};            // mapa code -> nombre (para "Banco origen")
  accounts: any[] = [];       // formas de pago (cuentas destino)
  list_payment_sub_status: any[] = [];

  // ===== Filtros de siempre (idénticos a la vista clásica) =====
  // Filtros INSTANTÁNEOS (se aplican al cambiar): estatus, forma de pago, sub-estatus.
  filter: any = {
    type: 'p',
    account: '',
    sub_status: 'NA'
  };

  // Lista de bancos para el select de "Banco origen" (además del mapa `banks` que rotula la tabla).
  bankList: any[] = [];

  // ===== Filtros de ACCIÓN (búsqueda puntual): requieren presionar "Buscar" =====
  // draft = lo que el usuario está escribiendo; applied = lo último aplicado (lo que SÍ viaja al backend).
  // Así, cambiar un filtro de arriba (instantáneo) NO aplica una búsqueda a medio escribir.
  searchDraft: any = { reference: '', bank: '', amount_min: null, amount_max: null };
  searchApplied: any = { reference: '', bank: '', amount_min: null, amount_max: null };

  /** ¿La referencia escrita es válida para buscar? (mín. 6 caracteres) */
  get refActive(): boolean { return `${this.searchDraft.reference || ''}`.trim().length >= 6; }
  /** ¿Escribió algo pero aún es muy corto? (1–5 chars) → mostrar aviso */
  get refTooShort(): boolean {
    const n = `${this.searchDraft.reference || ''}`.trim().length;
    return n > 0 && n < 6;
  }
  /** ¿Hay cambios en la búsqueda sin aplicar? → para resaltar "Buscar" y avisar */
  get searchDirty(): boolean {
    const norm = (v: any) => (v === null || v === undefined || v === '') ? '' : `${v}`.trim();
    const d = this.searchDraft, a = this.searchApplied;
    return norm(d.reference) !== norm(a.reference)
        || norm(d.bank) !== norm(a.bank)
        || norm(d.amount_min) !== norm(a.amount_min)
        || norm(d.amount_max) !== norm(a.amount_max);
  }
  /** ¿Hay alguna búsqueda APLICADA activa? → para mostrar estado */
  get searchActive(): boolean {
    const a = this.searchApplied;
    return !!(`${a.reference || ''}`.trim() || `${a.bank || ''}`.trim()
              || a.amount_min != null && `${a.amount_min}` !== ''
              || a.amount_max != null && `${a.amount_max}` !== '');
  }

  range: Array<Date> = [new Date(), new Date()];
  d1: any;
  d2: any;

  sortField = 'created_at';
  sortOrder: 'asc' | 'desc' = 'desc';

  // Modo de fecha — SOLO aplica para "Por conciliar" (type='p'):
  //  'range' = rango de fechas (como siempre) · 'all' = todo el histórico de por conciliar.
  dateMode: 'range' | 'all' = 'range';

  // Filtro múltiple de productos (idéntico a la vista clásica)
  productArray: any[] = [];
  filteredProductArray: any[] = [];
  selectedProducts: string[] = ['CREDIT'];
  tempSelectedProducts: string[] = ['CREDIT'];
  showDropdown = false;
  productSearchTerm = 'CREDIT';

  filterStates = {
    isLoadingProducts: false,
    isApplyingFilter: false,
    isLoadingResults: false,
    hasError: false,
    errorMessage: '',
    isInitialized: false
  };

  dropdownAnimation = { isOpening: false, isClosing: false };

  dateSetting3: MbscDatetimeOptions = {
    lang: 'es',
    theme: 'ios',
    themeVariant: 'light',
    max: now,
    responsive: {
      small: { controls: ['calendar'], display: 'center' },
      medium: { display: 'bubble' }
    }
  };

  // ===== Tabla / paginación =====
  rows: any[] = [];

  // Liquidación al comercio: QUITADA de la lista de pagos (Mario, 28-oct); se ve en la compra y en la cuota.
  total = 0;
  page = 1;
  limit = 50;
  pageSizeOptions = [20, 50, 100, 200];
  loading = false;          // recargas/paginación
  loadingInitial = true;    // primera carga
  loaded = false;

  selectedRow: any = null;
  loadcon: any;   // loading Aprobar
  loadcon2: any;  // loading Anular

  // Visor de comprobante (mismo comportamiento que la vista clásica)
  selectedFileUrl = '';
  selectedFileType = '';
  selectedFileName = '';
  selectedFileExtension = '';

  constructor(
    private router: Router,
    private _user: UserService,
    private datePipe: DatePipe,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    public _auth: AuthService,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    this.d1 = this.datePipe.transform(this.range[0], 'MM/dd/yyyy');
    this.d2 = this.datePipe.transform(this.range[1], 'MM/dd/yyyy');
    this.init();
  }

  async init() {
    this.loadingInitial = true;
    await this.getBanks();
    await this.consultAllProductsInvoices();
    this.updateSearchTermFromSelection();
    await this.getAccounts();
    await this.getSubStatus();
    await this.loadRows();
    this.loadingInitial = false;
  }

  // ============================ Carga base ============================

  getBanks = async () => {
    try {
      const resp = await this._user.getBanks();
      this.banks = {};
      this.bankList = [];
      if (resp?.res && Array.isArray(resp.res)) {
        // Lista para el select (solo bancos con bankCode), ordenada por nombre.
        this.bankList = resp.res
          .filter((b: any) => b?.bankCode && b?.name)
          .sort((a: any, b: any) => `${a.name}`.localeCompare(`${b.name}`));
        resp.res.forEach((bank: any) => {
          if (!bank?.name) return;
          // payment.bankCode usa el campo bankCode del banco; indexamos por ambos por seguridad.
          if (bank?.bankCode) this.banks[bank.bankCode] = bank.name;
          if (bank?.code) this.banks[bank.code] = bank.name;
        });
      }
    } catch (error) {
      console.error(error);
      this.banks = {};
      this.bankList = [];
    }
  };

  getAccounts = async () => {
    try {
      const resp = await this._user.account_payment();
      this.accounts = resp?.res || [];
      if (Array.isArray(this.accounts)) {
        this.accounts.sort((a: any, b: any) => {
          const nameA = a?.bank?.name?.toLowerCase() || '';
          const nameB = b?.bank?.name?.toLowerCase() || '';
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
      }
    } catch (error) {
      console.error(error);
      this.accounts = [];
    }
  };

  getSubStatus = async () => {
    try {
      const resp = await this._user.payment_sub_status();
      this.list_payment_sub_status = resp?.resp || [];
    } catch (error) {
      console.error(error);
      this.list_payment_sub_status = [];
    }
  };

  // ============================ Listado ============================

  private buildBody() {
    return {
      ...this.filter,
      ...this.searchApplied, // referencia/banco/monto: SOLO lo aplicado con "Buscar"
      // Todo el histórico de "Por conciliar": el backend omite la ventana de fechas.
      all_history: this.filter.type === 'p' && this.dateMode === 'all',
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      arr_typeProduct: this.selectedProducts
    };
  }

  private buildQuery() {
    return {
      page: this.page,
      limit: this.limit,
      init: this.d1,
      end: this.d2
    };
  }

  loadRows = async () => {
    this.loading = true;
    try {
      const resp = await this._user.listPaymentsCredits(this.buildBody(), this.buildQuery());
      this.rows = resp?.res || [];
      this.total = resp?.total || 0;
    } catch (error) {
      console.error(error);
      this.rows = [];
      this.total = 0;
    }
    this.loading = false;
    this.loaded = true;
    this.cdr.detectChanges();
  };

  // ============================ Filtros de siempre ============================

  setFilter() {
    this.page = 1;
    this.loadRows();
  }

  // Cambio de estatus: el modo "Todo el histórico" es exclusivo de "Por conciliar";
  // al salir de 'p' se vuelve a 'range' para no arrastrar ese modo a otros estatus.
  onStatusChange() {
    if (this.filter.type !== 'p') this.dateMode = 'range';
    this.setFilter();
  }

  // Alterna entre rango de fechas y todo el histórico (solo en "Por conciliar").
  setDateMode(mode: 'range' | 'all') {
    if (this.dateMode === mode) return;
    this.dateMode = mode;
    this.setFilter();
  }

  // Aplica la búsqueda (referencia/banco/monto): copia el borrador a "aplicado" y recarga.
  // La referencia exige mín. 6 chars (guarda).
  applySearch() {
    if (this.refTooShort) return;
    this.searchApplied = { ...this.searchDraft };
    this.setFilter();
  }

  // Limpia los filtros de búsqueda (borrador y aplicado) y recarga.
  clearSearch() {
    this.searchDraft = { reference: '', bank: '', amount_min: null, amount_max: null };
    this.searchApplied = { reference: '', bank: '', amount_min: null, amount_max: null };
    this.setFilter();
  }

  birthdaySet() {
    this.page = 1;
    this.d1 = this.datePipe.transform(this.range[0], 'MM/dd/yyyy');
    this.d2 = this.datePipe.transform(this.range[1], 'MM/dd/yyyy');
    this.loadRows();
  }

  changeSortOrder(order: 'asc' | 'desc') {
    if (this.sortOrder === order) return;
    this.sortOrder = order;
    this.page = 1;
    this.loadRows();
  }

  getSortIcon(): string { return this.sortOrder === 'asc' ? '↑' : '↓'; }
  getSortText(): string { return this.sortOrder === 'asc' ? 'Más Antiguo' : 'Más Reciente'; }

  // ============================ Filtro múltiple de productos (idéntico a la clásica) ============================

  async consultAllProductsInvoices() {
    try {
      this.filterStates.isLoadingProducts = true;
      this.filterStates.hasError = false;
      const consultProducts = await this._user.getTypesProductsInvoices();
      if (consultProducts && consultProducts.resp && Array.isArray(consultProducts.resp)) {
        this.productArray = consultProducts.resp;
        this.filteredProductArray = [...this.productArray];
        this.tempSelectedProducts = [...this.selectedProducts];
        this.filterStates.isInitialized = true;
      } else {
        this.productArray = [{ params: { code: 'CREDIT' } }];
        this.filteredProductArray = [...this.productArray];
        this.tempSelectedProducts = ['CREDIT'];
        this.filterStates.isInitialized = true;
      }
    } catch (error) {
      console.error(error);
      this.productArray = [{ params: { code: 'CREDIT' } }];
      this.filteredProductArray = [...this.productArray];
      this.tempSelectedProducts = ['CREDIT'];
      this.selectedProducts = ['CREDIT'];
      this.filterStates.isInitialized = true;
    } finally {
      this.filterStates.isLoadingProducts = false;
    }
  }

  async toggleDropdown() {
    if (this.showDropdown) this.closeDropdown(); else this.openDropdown();
  }

  async openDropdown() {
    if (this.filterStates.isLoadingProducts) return;
    this.tempSelectedProducts = [...this.selectedProducts];
    this.dropdownAnimation.isOpening = true;
    if (!this.productSearchTerm || this.productSearchTerm.trim() === '') {
      this.filteredProductArray = [...this.productArray];
    }
    setTimeout(() => {
      this.showDropdown = true;
      this.dropdownAnimation.isOpening = false;
    }, 50);
  }

  async closeDropdown() {
    this.dropdownAnimation.isClosing = true;
    setTimeout(() => {
      this.showDropdown = false;
      this.dropdownAnimation.isClosing = false;
      this.tempSelectedProducts = [...this.selectedProducts];
    }, 150);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.showDropdown) {
      const productSearchContainer = this.elementRef.nativeElement.querySelector('.product-search-input');
      if (productSearchContainer) {
        const clickedInside = productSearchContainer.contains(event.target as Node);
        if (!clickedInside) this.closeDropdown();
      }
    }
  }

  onProductSelectionChange(productCode: string, isChecked: boolean) {
    try {
      if (isChecked) {
        if (!this.tempSelectedProducts.includes(productCode)) this.tempSelectedProducts.push(productCode);
      } else {
        this.tempSelectedProducts = this.tempSelectedProducts.filter(code => code !== productCode);
      }
      if (this.tempSelectedProducts.length === 0) this.tempSelectedProducts = ['CREDIT'];
      this.updateSearchTermFromTempSelection();
    } catch (error) {
      console.error(error);
    }
  }

  updateSearchTermFromTempSelection() {
    if (this.tempSelectedProducts.length === 0) {
      this.productSearchTerm = '';
    } else if (this.tempSelectedProducts.length === 1) {
      this.productSearchTerm = this.tempSelectedProducts[0];
    } else if (this.tempSelectedProducts.length === 2) {
      this.productSearchTerm = `${this.tempSelectedProducts[0]} y ${this.tempSelectedProducts[1]}`;
    } else {
      this.productSearchTerm = `${this.tempSelectedProducts.length} productos seleccionados`;
    }
  }

  async applyFilters() {
    try {
      this.filterStates.isApplyingFilter = true;
      this.filterStates.isLoadingResults = true;
      this.filterStates.hasError = false;
      this.selectedProducts = [...this.tempSelectedProducts];
      this.updateSearchTermFromSelection();
      await this.closeDropdown();
      this.page = 1;
      await this.loadRows();
    } catch (error) {
      console.error(error);
      if (!this.rows || this.rows.length === 0) {
        this.filterStates.hasError = true;
        this.filterStates.errorMessage = 'Error al cargar pagos';
      }
    } finally {
      this.filterStates.isApplyingFilter = false;
      this.filterStates.isLoadingResults = false;
    }
  }

  isProductSelected(productCode: string): boolean {
    return this.tempSelectedProducts.includes(productCode);
  }

  getSelectedProductsText(): string {
    const count = this.selectedProducts.length;
    if (count === 0) return 'Seleccionar productos...';
    if (count === 1) return this.selectedProducts[0];
    if (count === 2) return `${this.selectedProducts[0]} y ${this.selectedProducts[1]}`;
    return `${count} productos seleccionados`;
  }

  async clearAllFilters() {
    this.tempSelectedProducts = ['CREDIT'];
    this.selectedProducts = ['CREDIT'];
    try {
      this.filterStates.isLoadingResults = true;
      this.page = 1;
      await this.loadRows();
    } catch (error) {
      console.error(error);
    } finally {
      this.filterStates.isLoadingResults = false;
    }
  }

  async retryOperation() {
    this.filterStates.hasError = false;
    this.filterStates.errorMessage = '';
    try {
      if (!this.filterStates.isInitialized) {
        await this.consultAllProductsInvoices();
      } else {
        await this.applyFilters();
      }
    } catch (error) {
      console.error(error);
      this.filterStates.hasError = false;
      this.filterStates.errorMessage = '';
    }
  }

  trackByProductCode(index: number, item: any): string {
    return item?.params?.code || index;
  }

  filterProductsSearch() {
    if (!this.productArray) { this.filteredProductArray = []; return; }
    if (!this.productSearchTerm || this.productSearchTerm.trim() === '') {
      this.filteredProductArray = [...this.productArray];
      return;
    }
    const term = this.productSearchTerm.toLowerCase().trim();
    this.filteredProductArray = this.productArray.filter((product: any) => {
      return (
        product?.params?.name?.toLowerCase().includes(term) ||
        product?.params?.code?.toLowerCase().includes(term) ||
        (product?.params?.dinamic_code &&
          product.params.dinamic_code.some((code: string) => code.toLowerCase().includes(term)))
      );
    });
  }

  async clearProductSearch() {
    this.productSearchTerm = '';
    this.filteredProductArray = [...this.productArray];
  }

  updateSearchTermFromSelection() {
    if (this.selectedProducts.length === 0) {
      this.productSearchTerm = '';
    } else if (this.selectedProducts.length === 1) {
      this.productSearchTerm = this.selectedProducts[0];
    } else if (this.selectedProducts.length === 2) {
      this.productSearchTerm = `${this.selectedProducts[0]} y ${this.selectedProducts[1]}`;
    } else {
      this.productSearchTerm = `${this.selectedProducts.length} productos seleccionados`;
    }
  }

  // ============================ Paginación ============================

  get totalPages(): number { return Math.max(1, Math.ceil(this.total / this.limit)); }
  get rangeStart(): number { return this.total === 0 ? 0 : ((this.page - 1) * this.limit) + 1; }
  get rangeEnd(): number { return Math.min(this.total, this.page * this.limit); }

  goToPage(p: number) {
    if (p < 1 || p > this.totalPages || p === this.page) return;
    this.page = p;
    this.loadRows();
  }

  changePageSize(size: any) {
    this.limit = Number(size) || 50;
    this.page = 1;
    this.loadRows();
  }

  get pageNumbers(): number[] {
    const pages: number[] = [];
    const tp = this.totalPages;
    let start = Math.max(1, this.page - 2);
    let end = Math.min(tp, start + 4);
    start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  trackByCode = (_: number, row: any) => row?.payment?.code;

  // ============================ Estatus (fiel a la vista clásica) ============================

  getStatusLabel(p: any): string {
    if (!p?.status) {
      if (p?.typeTransaction !== 'DOMICILIATION') {
        return p?.typeTransaction === 'AUTO' ? 'Transacción fallida' : 'Anulado';
      }
      return 'Rechazado por el Banco';
    }
    if (p?.reconciled_payment) {
      if (p?.payment_annulled) return 'Devolución';
      if (p?.typeTransaction !== 'DOMICILIATION') return 'Aprobado';
      if (p?.amount === 0) return p?.description || 'Rechazado';
      return 'Aprobado';
    }
    return p?.typeTransaction !== 'PENDING' ? 'Por conciliar' : 'En proceso de validación';
  }

  getStatusClass(p: any): string {
    if (!p?.status) return 'text-bg-danger';
    if (p?.reconciled_payment) {
      if (p?.payment_annulled) return 'text-bg-primary';
      if (p?.typeTransaction !== 'DOMICILIATION') return 'text-bg-success';
      if (p?.amount === 0) return 'text-bg-danger';
      return 'text-bg-success';
    }
    return 'text-bg-warning';
  }

  bankName(p: any): string { return (p?.bankCode && this.banks[p.bankCode]) || '-'; }

  // Detalle del origen (quien paga) para el tooltip.
  originTooltip(row: any): string {
    const p = row?.payment;
    if (!p) return 'Sin datos de origen';
    const lines: string[] = [];
    lines.push(`Banco: ${this.bankName(p)}`);
    if (p?.phone) lines.push(`Teléfono: ${p.phone}`);
    if (p?.account) lines.push(`Cuenta (Últ. 6): ${p.account}`);
    if (p?.cid && p?.cid !== 'V') lines.push(`Cédula/RIF: ${p.cid}`);
    if (p?.paymentMethodId) lines.push(`Referencia: ${p.paymentMethodId}`);
    return lines.length ? lines.join('\n') : 'Sin datos de origen';
  }

  // Banco/método de la cuenta destino (la que recibe el pago)
  destLabel(p: any): string {
    const a = p?.account_payment;
    return a?.bank?.name || a?.payment_method?.name || '-';
  }

  // Detalle de la cuenta receptora para el tooltip (el analista verifica que el
  // pago se registró a la cuenta destino correcta).
  destTooltip(row: any): string {
    const a = row?.payment?.account_payment;
    if (!a) return 'Sin cuenta destino registrada';
    const lines: string[] = [];
    lines.push(`Banco: ${a?.bank?.name || a?.payment_method?.name || '-'}`);
    if (a?.payment_method?.name) lines.push(`Método: ${a.payment_method.name}`);
    if (a?.number) lines.push(`Cuenta: ${a.number}`);
    if (a?.phone) lines.push(`Teléfono: ${a.phone}`);
    if (a?.tag) lines.push(`Etiqueta: ${a.tag}`);
    if (a?.currency?.code) lines.push(`Moneda: ${a.currency.code}`);
    return lines.join('\n');
  }

  // ============================ Detalle (reuso en modo modal) ============================

  openDetail(row: any) {
    this.selectedRow = row;
    this.modalService.open(this.detailModal, { size: 'xl', scrollable: true });
  }

  // ============================ Visor de comprobante (copia fiel de la vista clásica) ============================

  async redirectIMG(fileData: any, modalContent?: any): Promise<void> {
    let url: string;
    let fileType: string;
    let fileName: string;

    if (typeof fileData === 'string') {
      url = fileData;
      fileType = url.toLowerCase().includes('.pdf') ? 'pdf' : 'image';
      fileName = `comprobante_${Date.now()}`;
    } else if (fileData && fileData.url) {
      url = fileData.url;
      fileType = fileData.type_file || 'pdf';
      fileName = fileData.base_name || `comprobante_${Date.now()}`;
    } else {
      alertifyjs.error('Este pago no tiene comprobante adjunto');
      return;
    }

    if (!url) return;

    this.selectedFileUrl = url;
    this.selectedFileName = fileName;
    this.selectedFileExtension = fileType;

    const isPdf = fileType.toLowerCase() === 'pdf';

    if (isPdf) {
      const fileNameWithExt = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
      const blob = await this._user.downloadFile(url, fileType);
      if (blob) {
        saveAs(blob, fileNameWithExt);
      } else {
        alertifyjs.error('Error al descargar el archivo');
        window.open(url, '_blank');
      }
    } else {
      this.selectedFileType = 'image';
      if (modalContent) {
        this.modalService.open(modalContent, { size: 'xl', centered: true, backdrop: 'static' });
      }
    }
  }

  async onImageError(): Promise<void> {
    this.closeFileModal();
    const fileName = this.selectedFileName || `comprobante_${Date.now()}`;
    const fileExtension = this.selectedFileExtension || 'pdf';
    const fileNameWithExt = fileName.endsWith(`.${fileExtension}`) ? fileName : `${fileName}.${fileExtension}`;
    const blob = await this._user.downloadFile(this.selectedFileUrl, fileExtension);
    if (blob) {
      saveAs(blob, fileNameWithExt);
    } else {
      alertifyjs.error('Error al descargar el archivo');
      window.open(this.selectedFileUrl, '_blank');
    }
  }

  async downloadCurrentFile(): Promise<void> {
    const fileName = this.selectedFileName || `comprobante_${Date.now()}`;
    const fileExtension = this.selectedFileExtension || 'jpg';
    const fileNameWithExt = fileName.endsWith(`.${fileExtension}`) ? fileName : `${fileName}.${fileExtension}`;
    const blob = await this._user.downloadFile(this.selectedFileUrl, fileExtension);
    if (blob) {
      saveAs(blob, fileNameWithExt);
    } else {
      alertifyjs.error('Error al descargar el archivo');
    }
  }

  closeFileModal(): void {
    this.selectedFileUrl = '';
    this.selectedFileType = '';
    this.selectedFileName = '';
    this.selectedFileExtension = '';
    this.modalService.dismissAll();
  }

  // ============================ Quick actions (guards idénticos a la vista clásica) ============================

  canShowActions(p: any): boolean {
    return this._auth.hasAccessOrProfile('finanzas', ['OWNER', 'ANALIST'])
      && !p?.reconciled_payment && p?.status && p?.typeTransaction !== 'PENDING';
  }

  canApprove(): boolean {
    return this._auth.hasAnyPermission(['finanzas.verificacion_pagos.approve', 'pagos.pagos_registrados.approve'])
      || !this._auth.hasRbac$.getValue();
  }

  canCancel(): boolean {
    return this._auth.hasAnyPermission(['finanzas.verificacion_pagos.cancel', 'pagos.pagos_registrados.cancel'])
      || !this._auth.hasRbac$.getValue();
  }

  canEdit(): boolean {
    return this._auth.hasPermission('finanzas.verificacion_pagos.update') || !this._auth.hasRbac$.getValue();
  }

  // Copia fiel de concilidarPago (payments.component.ts).
  concilidarPago = async (payment: any) => {
    let confirm = false;
    const action = 'Conciliar';

    if (payment?.payments_similar && payment.payments_similar.length > 0) {
      Swal.fire({
        title: `Ya existe un pago con la misma referencia, cuenta destino y monto`,
        text: '¡No es posible conciliar!',
        icon: 'warning'
      }).then((result) => {
        if (result.isConfirmed) confirm = false;
      });
    } else {
      confirm = true;
    }

    if (confirm) {
      Swal.fire({
        title: `¿Deseas ${action} este pago?`,
        text: '¡No podrás revertir esto!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: `Si, ${action}!`
      }).then((result) => {
        if (result.isConfirmed) {
          this.concilidarPagoQuestion(payment?.code);
        }
      });
    }
  };

  concilidarPagoQuestion = async (code: any) => {
    this.loadcon = true;
    try {
      const resp = await this._user.reconciledPayment({ code_payment: code });
      if (resp?.res) alertifyjs.success(`Pago conciliado código ${code}`);
      await this.loadRows();
    } catch (error) {
      console.error(error);
    }
    this.loadcon = false;
  };

  // Copia fiel de cancelPayment (payments.component.ts).
  cancelPayment = async (code: any) => {
    const action = 'Anular';
    Swal.fire({
      title: '¿Deseas Anular este pago?',
      text: '¡No podrás revertir esto!',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      input: 'select',
      inputOptions: {
        'No se encuentra en banco': 'No se encuentra en banco',
        'Pago duplicado': 'Pago duplicado',
        'Txs verificada por Merchant': 'Txs verificada por Merchant',
        'Txs verificada manualmente': 'Txs verificada manualmente'
      },
      inputPlaceholder: 'Selecciona una razón',
      showCancelButton: true,
      confirmButtonText: `Si, ${action}!`,
      showLoaderOnConfirm: true,
      preConfirm: async (reason) => {
        try {
          if (!reason) return Swal.showValidationMessage(`Debe seleccionar una razón de anulación`);
        } catch (error) {
          Swal.showValidationMessage(`Request failed: ${error}`);
        }
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then((result) => {
      if (result.isConfirmed) {
        this.cancelPaymentQuestion(code, result.value);
      }
    });
  };

  cancelPaymentQuestion = async (code: any, msg = '') => {
    this.loadcon2 = true;
    try {
      const resp = await this._user.cancelPayment({ code_payment: code, description: msg });
      if (resp?.res) alertifyjs.success(`Pago anulado codigo ${code}`);
      await this.loadRows();
    } catch (error) {
      console.error(error);
    }
    this.loadcon2 = false;
  };

  // Copia fiel de setPayment (payments.component.ts): edición de pago.
  setPayment(item: any) {
    const action = 'Editar';
    Swal.fire({
      title: `¿Deseas ${action} este pago?`,
      text: '¡No podrás revertir esto!',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      input: 'select',
      inputOptions: {
        'Banco Receptor errado': 'Banco Receptor errado',
        'Banco Emisor errado': 'Banco Emisor errado',
        'Número de referencia errado': 'Número de referencia errado',
        'Fecha de Pago errada': 'Fecha de Pago errada',
        'Monto errado': 'Monto errado',
        'Comprobante de pago errado': 'Comprobante de pago errado'
      },
      inputPlaceholder: 'Selecciona una razón',
      showCancelButton: true,
      confirmButtonText: `Si, ${action}!`,
      showLoaderOnConfirm: true,
      preConfirm: async (reason) => {
        try {
          if (!reason) return Swal.showValidationMessage(`Debe seleccionar una razón de Edición`);
        } catch (error) {
          Swal.showValidationMessage(`Request failed: ${error}`);
        }
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then((result) => {
      if (result.isConfirmed) {
        item.description_edit = result?.value;
        localStorage.setItem('pe', JSON.stringify(item));
        this.router.navigate([`/console/post`], {});
      }
    });
  }

  // ============================ Header helpers ============================

  getIdentity(item: any) {
    if (!(item?.identitys?.length > 0)) return '';
    return `${item?.identitys[0]?.type_identity?.name} ${item?.identitys[0]?.code_identity?.name}-${item?.identitys[0]?.identity}`;
  }

  personName(row: any): string {
    const o = row?.owner || row?.payment?.owner;
    if (!o) return '-';
    return `${o?.name || ''} ${o?.lastName || ''}`.trim() || '-';
  }

  // Redirige al perfil del cliente (igual que la vista clásica), sin abrir el detalle del pago.
  goProfile(row: any, ev?: Event): void {
    ev?.stopPropagation();
    const code = this.ownerOf(row)?.code;
    if (!code) return;
    this.router.navigate(['/console/person/profile', code]);
  }

  // ===== Morosidad denormalizada del cliente (viene en owner.mora, igual que el perfil) =====
  private ownerOf(row: any): any { return row?.owner || row?.payment?.owner; }
  /** Mora actual (días) — MAX de cuotas pendientes. */
  moraActual(row: any): number { return Number(this.ownerOf(row)?.mora?.actual) || 0; }
  /** Mora efectiva (días) — mora activa tras el reconteo/reactivación. */
  moraEfectiva(row: any): number { return Number(this.ownerOf(row)?.mora?.effective) || 0; }
  /** ¿El cliente ya pagó reactivación? Solo entonces aplica la mora efectiva (reconteo). */
  hasReactivation(row: any): boolean { return this.ownerOf(row)?.mora?.has_reactivation_history === true; }
  /** Severidad del badge de mora actual (mismo criterio que el perfil: ≤45 / 46–60 / ≥61). */
  moraBadgeClass(d: number): string {
    if (d >= 61) return 'fpt-mora-high';
    if (d >= 46) return 'fpt-mora-mid';
    return 'fpt-mora-low';
  }

  avatarUrl(row: any) {
    const o = row?.payment?.owner || row?.owner;
    if (o?.file_selfie?.url) return this.sanitize(o.file_selfie.url);
    const fallback = o?.gender === 'F'
      ? '../assets/public/images/avatara-female.png'
      : '../assets/public/images/avatar.png';
    return this.sanitize(fallback);
  }

  accountLabel(item: any): string {
    if (!item) return '-';
    if (item?.bank) {
      return `${item?.bank?.name}${item?.tag ? ' (' + item?.tag + ')' : ''} (${item?.payment_method?.name})`;
    }
    return `${item?.payment_method?.name}${item?.tag ? ' (' + item?.tag + ')' : (item?.currency?.code ? ' (' + item?.currency?.code + ')' : '')}`;
  }

  sanitize(url: string) { return this.sanitizer.bypassSecurityTrustUrl(url); }

  // ============================ Navegación ============================

  goClassic() {
    this.router.navigate(['/console/payments/classic']);
  }
}
